import { CodeIcon, TerminalWindowIcon } from '@phosphor-icons/react';
import { Badge } from '~/components/ui/badge';
import { InlineMarkdown } from '~/components/inline-markdown';
import { PopoverContent } from '~/components/ui/popover';
import { cn } from '~/lib/utils';
import { BuildingTag } from './building-tag';
import { NewChatButton } from './new-chat-button';
import {
  ATTENTION_ORDER,
  actionVerbFor,
  boardRowLabel,
  formatBarTime,
  statusLineFor,
  waitingLineFor,
  type BarChat,
  type BarChatState,
  type BarGroup,
} from './state-mapping';
import {
  actionChipClass,
  STATE_COLOR_CLASS,
  STATE_GROUP_LABEL,
  STATE_PILL_CLASS,
  STATE_PILL_LABEL,
} from './state-style';

/** No real per-chat progress metric exists (decision #9 in the design doc) —
 *  every resting ring uses this same fallback arc. The state color is the
 *  signal, not the fraction. A `running` ring does not use it at all: it
 *  sweeps instead (see `StateRing`). */
const RING_FALLBACK_FRACTION = 0.6;

/**
 * The ring around a row's dot/icon.
 *
 * `running` rows sweep (2026-09-01 agentic-ring-sweep handoff, variant 1a):
 * a conic arc, bright at its leading edge and fading through violet behind
 * it, turning once every 2.6s over a faint blue track (`--eddy-ring-track`,
 * which is lifted on dark where a 10%-alpha hairline would vanish) — the same
 * motion the handoff specifies at 56px, taken here at the board's 28px. Every other
 * state keeps the static 60% arc in its own colour, unchanged.
 *
 * The sweep is a CSS conic gradient masked down to a ring (`.eddy-tb-sweep`
 * in `top-bar.css`) rather than an SVG stroke, because SVG has no angular
 * gradient — a stroke cannot fade along its own length. It is a sibling of
 * the SVG, not part of it, and rotates itself; the SVG below keeps drawing
 * the track so both rings agree on radius and width from the one `stroke`
 * prop.
 *
 * Nothing here has to stop the sweep when a process ends: a row that stops
 * running stops being `running`, so this re-renders as the static arc in the
 * terminal colour — the handoff's "never leave a spinning ring on a finished
 * task" falls out of the state mapping rather than needing its own timer.
 */
export function StateRing({
  state,
  size = 28,
  stroke = 2.5,
  children,
}: {
  state: BarChatState;
  size?: number;
  stroke?: number;
  children?: React.ReactNode;
}) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const sweeping = state === 'running';
  return (
    <span
      className="relative inline-flex shrink-0 items-center justify-center"
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} className="absolute inset-0 -rotate-90" aria-hidden="true">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={sweeping ? 'var(--eddy-ring-track)' : 'var(--line)'}
          strokeWidth={stroke}
        />
        {!sweeping && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke="currentColor"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${circ * RING_FALLBACK_FRACTION} ${circ}`}
            className={STATE_COLOR_CLASS[state]}
          />
        )}
      </svg>
      {sweeping && (
        <span
          data-testid="activity-ring-sweep"
          aria-hidden="true"
          className="eddy-tb-sweep absolute inset-0 rounded-full"
          style={{ '--eddy-tb-sweep-stroke': `${stroke}px` } as React.CSSProperties}
        />
      )}
      {children}
    </span>
  );
}

/**
 * A board group's heading: a dot in the group's state colour, the label, the
 * count. Exported (FRONT-2433) so the Chats page groups its full list under
 * exactly this heading; `label`/`marker` let it name a group the board has
 * no state for (Pinned, a date) while keeping the same look.
 */
export function GroupLabel({
  state,
  count,
  label,
  marker,
}: {
  state: BarChatState;
  count: number;
  label?: React.ReactNode;
  marker?: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-[7px] px-[12px] pt-[9px] pb-[3px]">
      {marker ?? <span className={cn('size-[6px] rounded-full bg-current', STATE_COLOR_CLASS[state])} />}
      <span className="text-xs font-semibold tracking-[0.08em] text-[var(--ink-2)] uppercase">
        {label ?? STATE_GROUP_LABEL[state]}
      </span>
      <span className="font-mono text-xs text-[var(--ink-3)]">{count}</span>
    </div>
  );
}

export function BoardRow({
  chat,
  onOpen,
  isCurrent,
  time: timeOverride,
  className,
}: {
  chat: BarChat;
  onOpen: (sessionId: string, title: string | null) => void;
  /** FRONT-2433: the right-hand stamp, when the caller needs more than the
   *  board's bare clock (the Chats page shows the date on older chats). */
  time?: string;
  /** FRONT-2433: extra classes for a caller that owns the row's surface. */
  className?: string;
  /**
   * FRONT-1579: marks the row for the chat the caller is currently sitting
   * in — only `ChatActivityMenu` (the in-modal board) ever passes this; the
   * top bar's own board has no "current chat" concept, so its rows are never
   * marked. Reuses `chat-pills.tsx`'s open-pill treatment (the `primary`
   * design token, not an invented color) rather than a new visual language
   * for the same idea.
   */
  isCurrent?: boolean;
}) {
  const time = timeOverride ?? formatBarTime(chat.lastActiveAt);
  const action = actionVerbFor(chat);
  const waitingLine = waitingLineFor(chat);
  return (
    <button
      type="button"
      onClick={() => onOpen(chat.sessionId, chat.title)}
      // FRONT-1429: the same fixed accessible name the classic pill rail got
      // in FRONT-1191. Without it a row is named by its own contents — title
      // plus a status line plus a relative timestamp — so its name changes as
      // the chat progresses and the clock moves, which is no name at all for a
      // screen reader and unlocatable for a spec. Now that the top bar is the
      // default, this board is the primary way to reopen a chat.
      aria-label={boardRowLabel(chat)}
      data-testid={chat.kind === 'pro-builder' ? 'board-row-pro-builder' : undefined}
      aria-current={isCurrent ? 'true' : undefined}
      className={cn(
        'group flex w-full items-center gap-[11px] rounded-[10px] px-[12px] py-[9px] text-left transition-colors hover:bg-[var(--bg-3)]',
        isCurrent && 'border border-primary/40 bg-primary/10 hover:bg-primary/15',
        className,
      )}
    >
      <StateRing state={chat.state}>
        {chat.kind === 'pro-builder' ? (
          // FRONT-3040: the Pro Builder row's own terminal icon, where a build
          // row shows its code icon. The same phosphor glyph as the Pro Builder
          // sidebar destination (sidebar-nav-catalog.ts).
          <TerminalWindowIcon
            size={12}
            weight="bold"
            aria-hidden
            className={cn(STATE_COLOR_CLASS[chat.state], chat.state === 'asking' && 'eddy-tb-blink')}
          />
        ) : chat.kind === 'build' ? (
          // FRONT-1681: a build row swaps the plain state dot for the app's
          // one "this is a build" icon, rather than a new one invented for
          // this row. (It used to point at the chat composer's coding button,
          // which the top bar's own Build button replaced on 2026-09-01.)
          <CodeIcon
            size={12}
            weight="bold"
            className={cn(STATE_COLOR_CLASS[chat.state], chat.state === 'asking' && 'eddy-tb-blink')}
          />
        ) : (
          <span
            className={cn(
              'size-[7px] rounded-full bg-current',
              STATE_COLOR_CLASS[chat.state],
              chat.state === 'asking' && 'eddy-tb-blink',
            )}
          />
        )}
      </StateRing>
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-center gap-[6px]">
          <span className="min-w-0 truncate text-[14.5px] font-medium text-[var(--ink-0)]">
            {chat.title}
          </span>
          {chat.directAgent && (
            // FRONT-2522: direct agent chat marker, same chip as the Chats page.
            <Badge
              variant="outline"
              data-testid="chat-row-direct-agent"
              className="shrink-0 max-w-[40%] py-0 text-[10px]"
            >
              <span className="truncate">Direct · {chat.directAgent.name}</span>
            </Badge>
          )}
          {chat.kind === 'pro-builder' &&
            // FRONT-3040: the same two-tag rule as a build row — the live
            // "$ building" chip only while Claude Code is working, else the
            // quiet kind badge in the same slot and style as "Coding session".
            (chat.state === 'running' ? (
              <BuildingTag />
            ) : (
              <Badge variant="outline" className="eddy-tb-kind-tag shrink-0 py-0 text-[10px]">
                Claude Code
              </Badge>
            ))}
          {chat.kind === 'build' &&
            // Two tags, never both (Mattias, 2026-09-01). While the session
            // is actually building, the handoff's terminal chip with its live
            // caret; otherwise the quiet violet kind marker, so a stopped or
            // finished session is still unmistakably a build rather than a
            // chat. A blinking caret on a session that stopped an hour ago
            // would be a lie, and no tag at all would lose the distinction.
            (chat.state === 'running' ? (
              <BuildingTag />
            ) : (
              // "Coding session", not "Build": the row's own subject is a
              // coding session everywhere else in the app — the window it
              // opens is named "Coding session — <project>" — and "Build"
              // beside a chat title read as a verb, not a kind.
              <Badge
                variant="outline"
                className="eddy-tb-kind-tag shrink-0 py-0 text-[10px]"
              >
                Coding session
              </Badge>
            ))}
        </span>
        <span className="mt-px block truncate text-[13px] text-[var(--ink-2)]">
          {waitingLine ? (
            <>
              {waitingLine.prefix} <InlineMarkdown as="span" text={waitingLine.text} />
            </>
          ) : (
            statusLineFor(chat)
          )}
        </span>
      </span>
      <span className="relative shrink-0">
        <span
          className={cn(
            'hidden items-center rounded-[7px] px-[9px] py-[4px] text-xs font-medium group-hover:inline-flex',
            actionChipClass(chat.state),
          )}
        >
          {action}
        </span>
        {time && (
          <span className="font-mono text-xs text-[var(--ink-3)] group-hover:hidden">{time}</span>
        )}
      </span>
    </button>
  );
}

interface ActivityBoardProps {
  groups: BarGroup[];
  onOpen: (sessionId: string, title: string | null) => void;
  /**
   * FRONT-1681: opens a Building Session row — separate from `onOpen`
   * because a build session id is not a chat session id, and routing it
   * through `onOpen` would try to open a chat that doesn't exist. Optional:
   * only the top bar's board ever renders build rows (`ChatActivityMenu`
   * keeps `includeBuilds` off), so a caller with no build rows has nothing
   * to wire this to. A build row with no `onOpenBuild` wired is inert on
   * click rather than misrouted through `onOpen`.
   */
  onOpenBuild?: (sessionId: string, title: string | null) => void;
  /**
   * FRONT-3040: opens the Pro Builder window from its row. Optional for the
   * same reason as `onOpenBuild`: a caller that never includes the row has
   * nothing to wire, and a row with no handler is inert on click.
   */
  onOpenProBuilder?: () => void;
  /**
   * FRONT-1579: optional, same reasoning as `onAllChats` below — a mount
   * site of the inbox chat modal's `ChatActivityMenu` that has no sensible
   * "start a fresh chat here" action passes nothing rather than a button
   * that renders and does nothing on click. The top bar always passes this,
   * so its own footer is unchanged.
   */
  onNew?: () => void;
  /**
   * FRONT-1579: optional so the inbox chat modal's `ChatActivityMenu` can
   * mount this same board without an "Open all chats" footer link — the
   * modal has nowhere sensible to send that (it IS a chat already open).
   * The top bar keeps passing it, so its own footer is unchanged.
   */
  onAllChats?: () => void;
  /**
   * FRONT-1579: the session id of the chat the caller is currently sitting
   * in, so that row can be marked instead of looking identical to every
   * other one (clicking it is a no-op — `ChatActivityMenu`'s own guard —
   * and an unmarked row that does nothing on click reads as broken). Only
   * `ChatActivityMenu` passes this; the top bar never does, so its own
   * rendering stays byte-identical.
   */
  currentSessionId?: string;
}

/**
 * FRONT-1681 (review round): the actual fallback for a build row when no
 * `onOpenBuild` is wired — makes the code match `onOpenBuild`'s own doc
 * ("inert on click"), which the previous `onOpenBuild!` non-null assertion
 * did not: that assertion is safe only as long as every future caller that
 * turns on build rows also remembers to wire `onOpenBuild`, which nothing
 * enforces, and a violation would have thrown on click rather than doing
 * nothing. Module-level so it's one stable reference, not a new closure
 * per render.
 */
function noopOpen(): void {}

/**
 * The dropdown that drops from the status cluster (README "Chat activity
 * board") — the full recent-chat list, grouped by state, available from
 * every section of the app. Renders as the `Popover`'s content, so it must
 * be mounted inside the same `<Popover>` as `StatusCluster`; Radix anchors
 * it under the trigger automatically (the design doc's open question #3 —
 * "don't hard-code a left offset" — falls out of that for free).
 */
export function ActivityBoard({
  groups,
  onOpen,
  onOpenBuild,
  onOpenProBuilder,
  onNew,
  onAllChats,
  currentSessionId,
}: ActivityBoardProps) {
  const countFor = (state: BarChatState) => groups.find((g) => g.state === state)?.items.length ?? 0;
  const openHandlerFor = (kind: string) => {
    if (kind === 'pro-builder') return onOpenProBuilder ?? noopOpen;
    if (kind === 'build') return onOpenBuild ?? noopOpen;
    return onOpen;
  };

  return (
    <PopoverContent
      align="start"
      sideOffset={8}
      aria-label="Chat activity"
      className="eddy-topbar eddy-tb-enter flex max-h-[calc(100vh-96px)] w-[440px] max-w-[calc(100vw-32px)] flex-col gap-0 overflow-hidden rounded-[16px] border border-[var(--line)] bg-[var(--bg-1)] p-0 shadow-lg"
    >
      <div className="shrink-0 border-b border-[var(--line)] bg-[var(--bg-2)] px-[16px] pt-[13px] pb-[11px]">
        <div className="flex items-center gap-[10px]">
          <span className="flex-1 text-[16px] font-semibold text-[var(--ink-0)]">Chat activity</span>
          <span className="font-mono text-xs text-[var(--ink-3)]">live</span>
          <span
            className="eddy-tb-pulse size-[7px] shrink-0 rounded-full bg-[var(--eddy-blue)]"
            aria-hidden="true"
          />
        </div>
        <div className="mt-[10px] flex flex-wrap gap-[6px]">
          {ATTENTION_ORDER.map((state) => {
            const count = countFor(state);
            if (count === 0) return null;
            return (
              <span
                key={state}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium',
                  STATE_PILL_CLASS[state],
                )}
              >
                {count} {STATE_PILL_LABEL[state]}
              </span>
            );
          })}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-[6px] pt-[4px] pb-[6px]">
        {groups.length ? (
          groups.map((g) => (
            <div key={g.state}>
              <GroupLabel state={g.state} count={g.items.length} />
              {g.items.map((c) => (
                <BoardRow
                  // FRONT-1681: chat and build session ids come from two
                  // different tables, so the bare sessionId alone is no
                  // longer guaranteed unique within this list.
                  key={`${c.kind}:${c.sessionId}`}
                  chat={c}
                  // FRONT-1681 (review round): a build row only ever
                  // appears in `groups` when the caller included builds
                  // (`useChatActivity({ includeBuilds: true })`), and the
                  // only caller that does (`ChatTopBar`) always wires
                  // `onOpenBuild`; the one caller that leaves it unset
                  // (`ChatActivityMenu`) never includes builds in the first
                  // place. So a build row with no `onOpenBuild` shouldn't
                  // occur in practice — but falling back to `noopOpen`
                  // rather than asserting it non-null is what actually
                  // makes that true: a future caller that violates the
                  // invariant gets an inert click, matching `onOpenBuild`'s
                  // own doc, instead of a runtime crash.
                  onOpen={openHandlerFor(c.kind)}
                  // A build row is never "the chat you're currently in" —
                  // currentSessionId is a chat session id (ChatActivityMenu's
                  // own openSessionId), so only a chat row may match it.
                  isCurrent={c.kind === 'chat' && c.sessionId === currentSessionId}
                />
              ))}
            </div>
          ))
        ) : (
          <div className="px-[16px] py-[26px] text-center text-[13px] text-[var(--ink-2)]">
            Nothing running. Start a chat and it shows up here.
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-[10px] border-t border-[var(--line)] px-[12px] py-[10px]">
        {onNew && <NewChatButton size="sm" onClick={onNew} />}
        <span className="flex-1" />
        {onAllChats && (
          <button
            type="button"
            onClick={onAllChats}
            className="text-[13px] font-medium text-[var(--eddy-blue-text)] hover:underline"
          >
            Open all chats →
          </button>
        )}
      </div>
    </PopoverContent>
  );
}
